/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, useEffect, useMemo } from "react"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { BaseDepartment, Department } from "types/models/menu"
import { SortDrawer } from "components/drawers/SortDrawer"
import { EventAction, EventCategory } from "constants/tracking"
import { MenuOverviewRow } from "components/common"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import {
  flattenInfiniteHierarchies,
  useMenuV3HierarchiesInfinite,
} from "queries"
import { FoodmaxapipbEntityType } from "generated"
import { mapHierarchyEntitiesToSubDepartments } from "utils/menuV3Mapper"
import {
  sendSortDrawerTrackingEvent,
  submitSortDepartments,
  useSortDepartmentsDrawerSetup,
} from "./sortDepartmentsShared"

const rowHeight = 64

interface SortSubDepartmentsDrawerProps {
  visible: boolean
  onClose(): void
}

export const SortSubDepartmentsDrawer: FC<SortSubDepartmentsDrawerProps> = ({
  visible,
  onClose,
}: SortSubDepartmentsDrawerProps) => {
  const {
    dispatch,
    sortedIndexes,
    setSortedIndexes,
    sortDepartmentsRtk,
    isRtkSortDepartmentsEnabled,
    isRtkFetchMenuEnabled,
    isRtkFlagLoading,
    fetchMenuRtk,
  } = useSortDepartmentsDrawerSetup()

  const {
    merchantID,
    overview: {
      department: parentDepartment,
      subDepartments: reduxSubDepartments,
    },
    isMartDepartmentStructure,
    isSellingTimeStructure,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    isUCS,
  } = useSelector((state: AppState) => state.menu)

  const enablePartialMenu = useEnablePartialMenu()

  // Fetch sub-departments from query when partial menu is enabled
  const { currentData: hierarchiesData } = useMenuV3HierarchiesInfinite(
    {
      parentType: FoodmaxapipbEntityType.Department,
      parentID: parentDepartment?.itemClassID,
      merchantID,
      merchantGroupID,
    },
    {
      skip: !enablePartialMenu || !parentDepartment?.itemClassID || !merchantID,
    },
  )

  // Use query sub-departments when partial menu is enabled, otherwise use Redux sub-departments
  const subDepartments = useMemo(() => {
    if (enablePartialMenu && hierarchiesData && parentDepartment?.itemClassID) {
      const allEntities = flattenInfiniteHierarchies(hierarchiesData)
      return mapHierarchyEntitiesToSubDepartments(
        allEntities || [],
        parentDepartment.itemClassID,
        parentDepartment.itemClassName,
      )
    }
    return reduxSubDepartments
  }, [
    enablePartialMenu,
    hierarchiesData,
    parentDepartment,
    reduxSubDepartments,
  ])

  useEffect(() => {
    setSortedIndexes([])
  }, [subDepartments, setSortedIndexes])

  const textMap = useTranslationsMap({
    sortCategoriesSuccess: "menu.notifications.sortCategories.successMessage",
  })

  const onSortEnd = (sortedDepartments: Department[]) => {
    sendSortDrawerTrackingEvent({
      category: EventCategory.menuOverviewRearrangeSubDepartment,
      action: EventAction.submitRearrangeSubDepartment,
      merchantID,
      merchantGroupID,
      menuGroupID,
      contextLabels: { CATEGORY_ID: parentDepartment?.itemClassID ?? "" },
      enablePartialMenu,
    })

    submitSortDepartments({
      dispatch,
      merchantID,
      merchantGroupID,
      menuGroupID,
      parentDepartmentID: parentDepartment?.itemClassID,
      isRtkFetchMenuEnabled,
      fetchMenuRtk,
      isMartDepartmentStructure,
      isSellingTimeStructure,
      enablePartialMenu,
      isRtkSortDepartmentsEnabled,
      sortDepartmentsRtk,
      successMessage: textMap.sortCategoriesSuccess,
      sortedItems: sortedDepartments,
      originalItems: subDepartments,
      sortedIndexes,
      isUCS,
      isRtkFlagLoading,
    })
  }

  return (
    <SortDrawer
      title={useTranslation("menu.drawers.sort.subCategories")}
      items={subDepartments}
      visible={visible}
      onClose={() => {
        sendSortDrawerTrackingEvent({
          category: EventCategory.menuOverviewRearrangeSubDepartment,
          action: EventAction.clickExit,
          merchantID,
          merchantGroupID,
          menuGroupID,
          contextLabels: { CATEGORY_ID: parentDepartment?.itemClassID ?? "" },
          enablePartialMenu,
        })
        onClose()
      }}
      onSave={onSortEnd}
      saveDisabled={isRtkFlagLoading}
      itemSize={rowHeight}
      renderRowContent={(department: BaseDepartment) => (
        <MenuOverviewRow
          name={department.itemClassName ?? ""}
          count={department.totalItems ?? 0}
          countNotAvailable={department.totalUnavailableItems}
        />
      )}
      onSort={({ oldIndex, newIndex }) => {
        sendSortDrawerTrackingEvent({
          category: EventCategory.menuOverviewRearrangeSubDepartment,
          action: EventAction.rearrangeSubDepartment,
          merchantID,
          merchantGroupID,
          menuGroupID,
          contextLabels: { CATEGORY_ID: parentDepartment?.itemClassID ?? "" },
          enablePartialMenu,
        })
        if (isUCS) {
          setSortedIndexes(prevState => [...prevState, [oldIndex, newIndex]])
        }
      }}
      onClear={() => {
        setSortedIndexes([])
      }}
      data-testid="sort-department-drawer"
    />
  )
}
